/**
 * useAppViewportCssVars
 *
 * Sets CSS custom properties for viewport height, prioritizing Telegram WebApp
 * viewport events over browser visualViewport. This prevents iOS "jumps" on
 * field-to-field focus by using Telegram's stable viewport measurements.
 *
 * Sets on document.documentElement:
 * - --app-viewport-height: Current viewport height in px
 * - --app-viewport-stable-height: Stable viewport height (doesn't change on keyboard type switch)
 * - --app-keyboard-height: Space occupied by keyboard (stableHeight - viewportHeight)
 */

import { useEffect, useRef } from 'react';
import { useViewportSource } from './viewportSource';

export function useAppViewportCssVars(): void {
  // Telegram's viewport inside the Mini App, none on the website (viewportSource.ts).
  const getViewport = useViewportSource();
  // Track max keyboard height while keyboard is open to prevent shrinking on type switch
  const maxKeyboardHeightRef = useRef(0);
  // Track max stable height to prevent layout collapse when Telegram reports a
  // smaller viewportStableHeight with the keyboard open (known Telegram iOS/Android
  // behaviour — viewportStableHeight updates to the new "stable" state with keyboard
  // visible, which shrinks the fixed-position .layout and collapses the page).
  const maxStableHeightRef = useRef(0);

  useEffect(() => {
    const webApp = getViewport();

    if (webApp) {
      // Telegram WebApp: use its viewport events
      const updateFromTelegram = () => {
        // Lock stable height first so the keyboard calc can use it.
        maxStableHeightRef.current = Math.max(
          maxStableHeightRef.current,
          webApp.viewportStableHeight,
        );

        // Keyboard height = locked full height minus current viewport height.
        // Must use the locked max, NOT raw viewportStableHeight — Telegram
        // shrinks both values together when the keyboard opens, so
        // (stableHeight - viewportHeight) would stay ~0.
        const currentKeyboardHeight = Math.max(0, maxStableHeightRef.current - webApp.viewportHeight);

        // Clamp to max while keyboard is open; reset when closed
        if (currentKeyboardHeight === 0) {
          // Keyboard closed - reset max
          maxKeyboardHeightRef.current = 0;
        } else {
          // Keyboard open - track max (prevents shrinking on keyboard type switch)
          maxKeyboardHeightRef.current = Math.max(maxKeyboardHeightRef.current, currentKeyboardHeight);
        }

        document.documentElement.style.setProperty(
          '--app-viewport-height',
          `${webApp.viewportHeight}px`
        );
        document.documentElement.style.setProperty(
          '--app-viewport-stable-height',
          `${maxStableHeightRef.current}px`
        );
        document.documentElement.style.setProperty(
          '--app-keyboard-height',
          `${maxKeyboardHeightRef.current}px`
        );
      };

      // Initial set
      updateFromTelegram();

      // Subscribe to Telegram viewport changes
      webApp.onEvent('viewportChanged', updateFromTelegram);

      return () => {
        webApp.offEvent('viewportChanged', updateFromTelegram);
      };
    } else {
      // Browser/dev fallback: use visualViewport.height only (no offset)
      const updateFromBrowser = () => {
        const height = window.visualViewport?.height ?? window.innerHeight;
        document.documentElement.style.setProperty('--app-viewport-height', `${height}px`);
        document.documentElement.style.setProperty('--app-viewport-stable-height', `${height}px`);
        document.documentElement.style.setProperty('--app-keyboard-height', '0px');
      };

      // Initial set
      updateFromBrowser();

      // Subscribe to resize events (not scroll - we don't track offset)
      const vv = window.visualViewport;
      if (vv) {
        vv.addEventListener('resize', updateFromBrowser);
      }
      window.addEventListener('resize', updateFromBrowser);

      return () => {
        if (vv) {
          vv.removeEventListener('resize', updateFromBrowser);
        }
        window.removeEventListener('resize', updateFromBrowser);
      };
    }
  }, [getViewport]);
}

// Keep old export name for backwards compatibility during transition
export { useAppViewportCssVars as useVisualViewportCssVars };
